import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  primary: [
    'bg-primary text-[color:var(--color-on-primary)] border border-transparent',
    'hover:bg-primary-600',
    'active:bg-primary-700',
    'focus-visible:ring-primary-soft',
  ].join(' '),
  secondary: [
    'bg-surface text-ink border border-border-soft',
    'hover:border-border-strong hover:bg-surface-sunken',
    'active:bg-border-soft',
    'focus-visible:ring-primary-soft',
  ].join(' '),
  ghost: [
    'bg-transparent text-ink border border-transparent',
    'hover:bg-primary-soft hover:text-primary',
    'active:bg-border-soft',
    'focus-visible:ring-primary-soft',
  ].join(' '),
  danger: [
    'bg-error text-white border border-transparent',
    'hover:bg-error-600',
    'active:bg-error-700',
    'focus-visible:ring-error/30',
  ].join(' '),
  link: [
    'bg-transparent text-primary border border-transparent px-0 h-auto',
    'hover:underline underline-offset-4',
    'focus-visible:ring-primary-soft',
  ].join(' '),
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-sm',
  md: 'h-10 px-5 text-base gap-2 rounded-md',
  lg: 'h-12 px-6 text-md gap-2 rounded-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'primary',
    size = 'md',
    loading,
    disabled,
    iconLeft,
    iconRight,
    fullWidth,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-medium tracking-normal whitespace-nowrap select-none',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-enter',
        'focus-visible:outline-none focus-visible:ring-4',
        'disabled:opacity-50 disabled:pointer-events-none',
        'active:scale-[0.98] motion-reduce:active:scale-100',
        variant !== 'link' && sizes[size],
        variants[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : iconLeft}
      {children}
      {!loading && iconRight}
    </button>
  );
});
